import { ReactNode } from "react";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";

import { styled } from "@mui/material/styles";
import { ChevronLeft, ChevronRight, InfoIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { LineChart, LineChartData } from "components/charts";
import { useFormat } from "hooks/useFormat";

import { useHistoryStore } from "../historyStore";

const Wrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(4),
  paddingInline: theme.spacing(5),
  paddingBlock: theme.spacing(4),
  borderWidth: 1,
  pageBreakAfter: "always",
  borderStyle: "solid",
  borderColor: theme.vars.palette.borders.neutral.subtle,
  backgroundColor: theme.vars.palette.background.paper,
  borderRadius: theme.spacing(1.5),
  [theme.breakpoints.down("sm")]: {
    gap: theme.spacing(1.5),
    padding: theme.spacing(2.5),
  },
}));

type StatisticProps = {
  title: string;
  value: string;
};

function Statistic({ title, value }: StatisticProps) {
  return (
    <Box>
      <Typography variant={"bodySmallRegular"}>{title}</Typography>
      <Typography variant={"bodyLargeMedium"}>{value}</Typography>
    </Box>
  );
}

type HistoryGraphProps = {
  icon: ReactNode;
  title: string;
  data: LineChartData;
  description?: string;
};

export function HistoryGraph({
  icon,
  title,
  data,
  description,
}: HistoryGraphProps) {
  const { t } = useTranslation();
  const { format } = useFormat();

  const backCount = useHistoryStore((s) => s.backCount);
  const setBackCount = useHistoryStore((s) => s.setBackCount);

  const highest = data.data.reduce((s, d) => Math.max(s, d.value), -Infinity);
  const lowest = data.data.reduce((s, d) => Math.min(s, d.value), Infinity);

  const hasData = data.data.length > 0;
  const showYear =
    data.xDomain[0].getFullYear() !== data.xDomain[1].getFullYear();
  const dateFormat = `do MMM${showYear ? " y" : ""}`;

  return (
    <Wrapper>
      <Stack>
        {icon}
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          paddingBlock={1}
        >
          <Typography variant={"headingSmallSemibold"}>{title}</Typography>
          {description && (
            <Tooltip title={description}>
              <InfoIcon />
            </Tooltip>
          )}
        </Stack>
        <Stack direction={"row"} alignItems={"center"} gap={1}>
          <IconButton
            size={"small"}
            disableRipple
            color={"accent.soft"}
            onClick={() => setBackCount(backCount + 1)}
            aria-label={"Previous period"}
          >
            <ChevronLeft />
          </IconButton>
          <Typography variant={"bodySmallRegular"}>
            {format(data.xDomain[0], dateFormat)}
            {" - "}
            {format(data.xDomain[1], dateFormat)}
          </Typography>
          <IconButton
            size={"small"}
            disableRipple
            color={"accent.soft"}
            disabled={backCount === 0}
            onClick={() => setBackCount(backCount - 1)}
            aria-label={"Next period"}
          >
            <ChevronRight />
          </IconButton>
        </Stack>
      </Stack>
      <Stack direction={"row"} gap={3}>
        <Statistic
          title={t("Average")}
          value={data.average ? data.formatter(data.average) : " - "}
        />
        <Statistic
          title={t("Highest")}
          value={hasData ? data.formatter(highest) : " - "}
        />
        <Statistic
          title={t("Lowest")}
          value={hasData ? data.formatter(lowest) : " - "}
        />
      </Stack>
      <Box height={"250px"}>
        <LineChart data={data} />
      </Box>
    </Wrapper>
  );
}
